import {
  WEEK_ORDER,
  WEEKDAY_LONG,
  WEEKDAY_PLURAL,
  WEEKDAY_SHORT,
  formatDateKey,
  listWords,
  uaePublicHoliday,
  weekdayOf,
  type DraftClosure,
  type DraftItem,
  type TimetableDraft,
} from "@teamna/shared";
import { useState, type ReactNode } from "react";
import { Avatar, Button, Field, Switch } from "../../components/index.ts";
import { CHIPS_UP_TO, CoachPicker, type CoachOption } from "./CoachPicker.tsx";
import type { BoardGroup } from "./WeekBoard.tsx";
import { bookedNextOn, bookedOnDate, coachesFirst, levelCoaches, timeLabel, type BookedIndex } from "./model.ts";

/** The words of the business: "level", "coach", "swimmers", and who books: "family" or "client". */
export type RailWords = { group: string; groupPlural: string; staff: string; staffPlural: string; people: string; family: string; families: string };

/** A person of the team, with the photo for the face on a coach chip. */
export type TeamPerson = { userId: string; name: string; photoUrl?: string | null };

/** A team member as a coach choice: the name, and the face before it. */
export function teamOption(member: TeamPerson): CoachOption {
  return { value: member.userId, label: member.name, photoUrl: member.photoUrl ?? null };
}

/** The face before a person's name on a chip, when the option is a person (`photoUrl` is set, null included). */
function ChipFace({ option }: { option: { label: string; photoUrl?: string | null } }) {
  return option.photoUrl !== undefined ? <Avatar name={option.label} photoUrl={option.photoUrl} size={16} /> : null;
}

export const chip =
  "rounded-full border-[1.5px] px-2.5 py-1 text-[12px] font-semibold transition-colors focus-visible:outline-3 focus-visible:outline-accent-soft disabled:cursor-default disabled:opacity-45";
export const chipOn = "border-ink bg-sunken text-ink";
export const chipOff = "border-line-strong bg-paper text-ink-2 hover:border-ink-3";

export function Chips<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  /** `title`: the full words on hover, when the chip shows a short label. `photoUrl`: a person's face. */
  options: Array<{ value: T; label: string; title?: string; photoUrl?: string | null }>;
  value: T;
  onChange: (value: T) => void;
  /** A change is on its way: the chips wait. */
  disabled?: boolean;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          title={option.title}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={[chip, "inline-flex items-center gap-1.5", value === option.value ? chipOn : chipOff].join(" ")}
        >
          <ChipFace option={option} />
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

/** The coach of a time: chips for a small team, a picker with search for a large one. */
export function CoachChoice({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  options: CoachOption[];
  onChange: (value: string) => void;
  /** A change is on its way: the choice waits. */
  disabled?: boolean;
}) {
  return options.filter((option) => option.value !== "").length > CHIPS_UP_TO ? (
    <CoachPicker label={label} value={value} options={options} onChange={onChange} disabled={disabled} />
  ) : (
    <Chips label={label} value={value} options={options} onChange={onChange} disabled={disabled} />
  );
}

/** A level's coaches first, under a heading in the picker, then everyone else. */
export function coachOptions(team: TeamPerson[], group: { name: string; staffUserIds: readonly string[] }, staffPlural: string): CoachOption[] {
  return coachesFirst(team, group.staffUserIds).map((member) =>
    Object.assign(teamOption(member), {
      group: group.staffUserIds.length === 0 ? undefined : group.staffUserIds.includes(member.userId) ? `${group.name} ${staffPlural}` : "Everyone else",
    }),
  );
}

/** Chips where more than one can be on: the coaches of a level. */
function MultiChips({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string; photoUrl?: string | null }>;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => {
        const on = value.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((id) => id !== option.value) : [...value, option.value])}
            className={[chip, "inline-flex items-center gap-1.5", on ? chipOn : chipOff].join(" ")}
          >
            <ChipFace option={option} />
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
}

function Stepper({
  label,
  value,
  onStep,
  canDown,
  canUp,
}: {
  label: string;
  value: string;
  onStep: (direction: -1 | 1) => void;
  canDown: boolean;
  canUp: boolean;
}) {
  return (
    <fieldset className="m-0 inline-flex min-w-0 items-center overflow-hidden rounded-[10px] border border-line-strong bg-paper p-0">
      <legend className="sr-only">{label}</legend>
      <button
        type="button"
        aria-label={`${label}: less`}
        disabled={!canDown}
        onClick={() => onStep(-1)}
        className="h-8 w-8 text-[16px] text-accent-strong disabled:text-line-strong"
      >
        −
      </button>
      <b className="min-w-[60px] text-center text-[14px] tabular-nums" aria-live="polite">
        {value}
      </b>
      <button
        type="button"
        aria-label={`${label}: more`}
        disabled={!canUp}
        onClick={() => onStep(1)}
        className="h-8 w-8 text-[16px] text-accent-strong disabled:text-line-strong"
      >
        +
      </button>
    </fieldset>
  );
}

/** The × of the card a block opens in. */
function CloseCard({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="secondary" size="sm" aria-label="Close" title="Close (Esc)" onClick={onClick} className="ml-auto shrink-0 px-2.5">
      ×
    </Button>
  );
}

/**
 * The session's own name and what it is, as parents see it on the Book tab ("Core awake",
 * "Mixed apparatus"). Both optional: with no name, parents see the class name. A change is one step
 * of the draft when the field loses focus, so typing does not fill the undo list.
 */
function SessionName({ item, onChange }: { item: DraftItem; onChange: (label: string, next: DraftItem) => void }) {
  const [name, setName] = useState(item.name ?? "");
  const [detail, setDetail] = useState(item.detail ?? "");
  const commit = () => {
    const nextName = name.trim() || null;
    const nextDetail = detail.trim() || null;
    if (nextName !== (item.name ?? null) || nextDetail !== (item.detail ?? null)) {
      onChange("the session name", { ...item, name: nextName, detail: nextDetail });
    }
  };
  return (
    <div className="grid gap-2">
      <Field
        label="Session name (optional)"
        size="compact"
        value={name}
        maxLength={60}
        placeholder="Core awake"
        hint="Parents see it when they book. Empty: the class name."
        onChange={(event) => setName(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
      />
      <Field
        label="What it is (optional)"
        size="compact"
        value={detail}
        maxLength={80}
        placeholder="Mixed apparatus"
        onChange={(event) => setDetail(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
      />
    </div>
  );
}

export function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-[11.5px] font-bold text-ink-2">{label}</span>
      {children}
    </div>
  );
}

export function Back({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick} className="justify-self-start px-0 whitespace-nowrap">
      ‹ {children}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// The levels
// ---------------------------------------------------------------------------

/** The next level in list order (after `current`, round the list) that has no weekly time yet. */
export function nextLevelWithoutTimes(groups: readonly BoardGroup[], draft: TimetableDraft, current: string | null): BoardGroup | null {
  const from = groups.findIndex((group) => group.id === current);
  for (let step = 1; step <= groups.length; step += 1) {
    const group = groups[(from + step) % groups.length]!;
    if (group.id !== current && !draft.items.some((item) => item.groupId === group.id)) return group;
  }
  return null;
}

/** "Mon, Wed 4:00 PM", or for free hours "Sara, Mon 3:00 PM to 5:00 PM". */
function timeChipLabel(item: DraftItem, personName: (userId: string) => string): string {
  const days = [...item.days].sort((a, b) => WEEK_ORDER.indexOf(a) - WEEK_ORDER.indexOf(b)).map((day) => WEEKDAY_SHORT[day]!).join(", ");
  return item.free
    ? `${item.staffUserId ? personName(item.staffUserId) : "No one yet"}, ${days} ${timeLabel(item.start)} to ${timeLabel(item.end)}`
    : `${days} ${timeLabel(item.start)}`;
}

/**
 * The levels, always on the rail: each one with its weekly times, the picked one open with its
 * coach, its times and the way to the next level. A block opens in a card next to the week, so the
 * list never goes away (prototype `teamna/prototypes/timetable-flow-v2.html`, approved 2026-10-17).
 */
export function LevelList({
  groups,
  draft,
  brush,
  team,
  words,
  until,
  canReview,
  onBrush,
  onNext,
  onOpen,
  onAdd,
  onReview,
  personName,
}: {
  groups: BoardGroup[];
  draft: TimetableDraft;
  brush: { groupId: string; staffUserId: string | null } | null;
  team: TeamPerson[];
  words: RailWords;
  until: string;
  /** The draft has changes to publish. */
  canReview: boolean;
  onBrush: (brush: { groupId: string; staffUserId: string | null } | null) => void;
  /** Picks the next level with no times. */
  onNext: (group: BoardGroup) => void;
  /** Opens one weekly time in the card. */
  onOpen: (key: string) => void;
  onAdd: () => void;
  onReview: () => void;
  personName: (userId: string) => string;
}) {
  const Group = words.group.charAt(0).toUpperCase() + words.group.slice(1);
  const withTimes = groups.filter((group) => draft.items.some((item) => item.groupId === group.id));
  const allDone = groups.length > 0 && withTimes.length === groups.length;
  const next = brush ? nextLevelWithoutTimes(groups, draft, brush.groupId) : null;
  const firstBrush = (group: BoardGroup) => ({
    groupId: group.id,
    staffUserId: group.kind === "private" ? (team[0]?.userId ?? null) : (levelCoaches(group, draft)[0] ?? null),
  });
  return (
    <div className="grid gap-2.5">
      <div className="grid gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <b className="text-[14px] capitalize">{words.groupPlural}</b>
          {groups.length > 0 ? (
            <span className="text-[11.5px] text-ink-2 tabular-nums">
              {withTimes.length} of {groups.length} have times
            </span>
          ) : null}
        </div>
        {groups.length > 1 ? (
          <div className="grid auto-cols-fr grid-flow-col gap-[3px]" aria-hidden="true">
            {groups.map((group) => (
              <i
                key={group.id}
                className="h-[5px] rounded-full bg-line"
                style={withTimes.includes(group) ? { background: group.color } : undefined}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* At the end of the level by level flow, the way on: the review (the bar under the week has it too). */}
      {allDone && brush ? (
        <div className="grid gap-2 rounded-xl bg-confirmed-surface px-2.5 py-2 text-[12.5px] font-semibold text-confirmed">
          All {groups.length} {words.groupPlural} have times.
          {canReview ? (
            <Button size="sm" onClick={onReview}>
              Review the changes
            </Button>
          ) : null}
        </div>
      ) : null}

      {groups.map((group) => {
        const items = draft.items.filter((item) => item.groupId === group.id);
        const weekly = items.reduce((total, item) => total + item.days.length, 0);
        const on = brush?.groupId === group.id;
        const times = [...items].sort(
          (a, b) => WEEK_ORDER.indexOf(Math.min(...a.days.map((day) => WEEK_ORDER.indexOf(day)))) - WEEK_ORDER.indexOf(Math.min(...b.days.map((day) => WEEK_ORDER.indexOf(day)))) || a.start - b.start,
        );
        return (
          <div
            key={group.id}
            className={["grid rounded-xl border-[1.5px]", on ? "border-accent bg-accent-surface" : "border-line bg-paper hover:border-line-strong"].join(" ")}
          >
            <button
              type="button"
              aria-pressed={on}
              data-level={group.id}
              onClick={() => onBrush(on ? null : firstBrush(group))}
              className="grid grid-cols-[12px_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-xl px-2.5 py-2 text-left"
            >
              <i className="row-span-2 h-8 w-3 rounded" style={{ background: group.color }} aria-hidden="true" />
              <b className="truncate text-[14px]">{group.name}</b>
              <span className={["row-span-2 text-right text-[11.5px] tabular-nums", items.length ? "font-bold text-confirmed" : "text-ink-3"].join(" ")}>
                {items.length === 0
                  ? "No times"
                  : group.kind === "private"
                    ? `✓ ${items.length} ${items.length === 1 ? "block" : "blocks"}`
                    : `✓ ${weekly} a week`}
              </span>
              <small className="col-start-2 text-[11.5px] text-ink-2">
                {group.kind === "private"
                  ? `Free hours per ${words.staff}, ${group.durationMin} minute sessions`
                  : [
                      `${group.durationMin} min`,
                      group.staffUserIds.length > 2 ? `${group.staffUserIds.length} ${words.staffPlural}` : listWords(group.staffUserIds.map(personName)),
                    ]
                      .filter(Boolean)
                      .join(", ")}
              </small>
            </button>
            {on && brush ? (
              <div className="grid gap-2.5 px-2.5 pb-2.5">
                {/* Anyone on the team, the level's coaches first: a level with one coach can still share its times. */}
                {group.kind === "group" && team.length > 1 ? (
                  <FieldBlock label={`${words.staff.charAt(0).toUpperCase()}${words.staff.slice(1)} for new times`}>
                    <CoachChoice
                      label={`${words.staff} for new times`}
                      value={brush.staffUserId ?? ""}
                      options={coachOptions(team, group, words.staffPlural)}
                      onChange={(userId) => onBrush({ groupId: group.id, staffUserId: userId })}
                    />
                  </FieldBlock>
                ) : null}
                {group.kind === "private" ? (
                  <FieldBlock label="Free hours for">
                    <CoachChoice
                      label="Free hours for"
                      value={brush.staffUserId ?? ""}
                      options={team.map(teamOption)}
                      onChange={(userId) => onBrush({ groupId: group.id, staffUserId: userId })}
                    />
                  </FieldBlock>
                ) : null}
                <p className="rounded-[10px] bg-paper px-2.5 py-2 text-[12px] text-accent-strong">
                  {group.kind === "private"
                    ? `Click the week where ${brush.staffUserId ? personName(brush.staffUserId) : "the person"} is free. Teamna adds 2 hours. ${words.families.charAt(0).toUpperCase()}${words.families.slice(1)} book ${group.durationMin} minute sessions in them.`
                    : `Click the week to add a ${group.name} time. It repeats every week until ${until}. ${group.name} stays picked, so you can add more times.`}
                </p>
                {times.length > 0 ? (
                  <FieldBlock label={`${group.name} times`}>
                    <div className="flex flex-wrap gap-1.5">
                      {times.map((item) => (
                        <Button key={item.key} variant="secondary" size="sm" onClick={() => onOpen(item.key)} className="tabular-nums">
                          {timeChipLabel(item, personName)}
                        </Button>
                      ))}
                    </div>
                  </FieldBlock>
                ) : null}
                {next ? (
                  <>
                    <Button variant={items.length ? "primary" : "secondary"} fullWidth onClick={() => onNext(next)}>
                      Next {words.group}: {next.name} →
                    </Button>
                    {items.length === 0 ? (
                      <span className="text-center text-[11.5px] text-ink-3">{group.name} has no times yet. You can come back to it.</span>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      <Button variant="ghost" size="sm" onClick={onAdd} className="justify-self-start px-0">
        + Add a {words.group}
      </Button>
      {brush ? null : (
        <p className="rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">
          {groups.length > 0 ? `Pick a ${words.group}, then click the week. ` : `${Group}s hold the weekly times. Add one to start. `}
          Click a block to change its days, time, places or {words.staff}. Drag it to move it. Striped blocks are when a {words.staff} is free for
          private sessions.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One weekly time
// ---------------------------------------------------------------------------

export function ItemEditor({
  item,
  before,
  group,
  coaches,
  team,
  words,
  booked,
  daysOff,
  sessionsUntil,
  onClose,
  onChange,
  onDayStaff,
  siblings,
  onSelect,
  onUndo,
  onRemove,
}: {
  item: DraftItem;
  /** The item as parents see it. Null for a new one. */
  before: DraftItem | null;
  group: BoardGroup;
  /** The level's coaches, then anyone who has one of its weekly times (`levelCoaches`). */
  coaches: string[];
  team: TeamPerson[];
  words: RailWords;
  booked: BookedIndex;
  daysOff: readonly number[];
  /** "8 sessions until Thu 17 Dec" */
  sessionsUntil: string;
  /** Closes the card. */
  onClose: () => void;
  onChange: (label: string, next: DraftItem) => void;
  /** Gives one day of a weekly time (this one or a sibling, by `key`) another coach: the day may move to another block. */
  onDayStaff: (key: string, weekday: number, staffUserId: string | null) => void;
  /** Other weekly times of this level at the same time, with other coaches. */
  siblings: DraftItem[];
  onSelect: (key: string) => void;
  onUndo: () => void;
  onRemove: () => void;
}) {
  const Staff = words.staff.charAt(0).toUpperCase() + words.staff.slice(1);
  // The level's own coaches first, then the rest of the team. The order never follows a pick: a
  // chip does not move under the owner's pointer.
  const ordered = coachOptions(team, group, words.staffPlural);
  const teamOptions: CoachOption[] = [...ordered, ...(item.free ? [] : [{ value: "", label: "Later" }])];
  // On each day, only the level's own coaches when it has several: the row stays short. A pick
  // never changes which chips show (a level with one coach on its list shows the whole team).
  const dayOptions =
    group.staffUserIds.length > 1
      ? ordered.filter((option) => group.staffUserIds.includes(option.value) || [item, ...siblings].some((row) => row.staffUserId === option.value))
      : teamOptions;
  const [split, setSplit] = useState(false);
  // Every day of the level at this time, from this block and its siblings: a day that changes
  // coach moves to another block, and its row stays where it was.
  const dayRows = [item, ...siblings]
    .flatMap((holder) => holder.days.map((day) => ({ day, holder })))
    .sort((a, b) => WEEK_ORDER.indexOf(a.day) - WEEK_ORDER.indexOf(b.day));
  // A level with several coaches (on its list or on its times) opens on a coach for each day, and
  // stays there after a pick on a day.
  const perDay = !item.free && dayRows.length > 1 && (split || coaches.length > 1 || siblings.length > 0);
  const changed =
    before !== null &&
    (before.start !== item.start ||
      before.end !== item.end ||
      before.capacity !== item.capacity ||
      before.staffUserId !== item.staffUserId ||
      (before.name ?? null) !== (item.name ?? null) ||
      (before.detail ?? null) !== (item.detail ?? null) ||
      before.days.join() !== item.days.join());
  const step = item.free ? group.durationMin : 15;
  const bookedDays = (before?.days ?? []).map((day) => ({ day, count: bookedNextOn(before!, day, booked) })).filter((row) => row.count > 0);
  const nameOf = (userId: string | null) => team.find((member) => member.userId === userId)?.name ?? "No one yet";
  const mostBooked = Math.max(0, ...item.days.map((day) => bookedNextOn(item, day, booked)));

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-2">
        <i className="h-6 w-2.5 rounded" style={{ background: group.color }} aria-hidden="true" />
        <h2 className="text-[16px] font-bold">{item.free ? `${nameOf(item.staffUserId)}, private` : group.name}</h2>
        {before === null ? (
          <span className="rounded-full bg-accent-surface px-2 py-0.5 text-[10.5px] font-bold text-accent-strong">New</span>
        ) : changed ? (
          <span className="rounded-full bg-late-surface px-2 py-0.5 text-[10.5px] font-bold text-late">Changed</span>
        ) : null}
        <CloseCard onClick={onClose} />
      </div>

      {/* A new key on an outside change (undo, redo) starts the fields from the draft again (F5.5). */}
      <SessionName key={JSON.stringify([item.key, item.name ?? null, item.detail ?? null])} item={item} onChange={onChange} />

      <FieldBlock label="Every week on">
        <fieldset className="m-0 grid min-w-0 grid-cols-7 gap-1 border-0 p-0">
          <legend className="sr-only">Every week on</legend>
          {WEEK_ORDER.map((day) => {
            const off = daysOff.includes(day);
            const on = item.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                disabled={off || (on && item.days.length === 1)}
                aria-pressed={on}
                aria-label={WEEKDAY_LONG[day]}
                title={off ? "Day off" : on && item.days.length === 1 ? "A weekly time needs one day" : undefined}
                onClick={() =>
                  onChange(
                    on ? "the day taken off" : "the added day",
                    { ...item, days: on ? item.days.filter((row) => row !== day) : [...item.days, day] },
                  )
                }
                className={[
                  "rounded-lg border-[1.5px] py-1 text-[11.5px] font-bold",
                  off ? "border-dashed border-line-strong text-ink-3" : on ? "sk-tile [--sk-p:var(--color-accent)] border-transparent text-inverse" : "sk-chip text-ink-2",
                ].join(" ")}
              >
                {WEEKDAY_SHORT[day]}
              </button>
            );
          })}
        </fieldset>
      </FieldBlock>

      <div className="grid grid-cols-2 items-start gap-2.5">
        <FieldBlock label={item.free ? "From" : "Starts"}>
          <Stepper
            label={item.free ? "From" : "Starts"}
            value={timeLabel(item.start)}
            canDown={item.start - step >= 0}
            canUp={item.free ? item.start + step < item.end : item.end + 15 <= 24 * 60}
            onStep={(direction) =>
              onChange(
                item.free ? "the hours change" : "the time change",
                item.free
                  ? { ...item, start: item.start + direction * step }
                  : { ...item, start: item.start + direction * 15, end: item.end + direction * 15 },
              )
            }
          />
        </FieldBlock>
        {item.free ? (
          <FieldBlock label="To">
            <Stepper
              label="To"
              value={timeLabel(item.end)}
              canDown={item.end - step > item.start}
              canUp={item.end + step <= 24 * 60}
              onStep={(direction) => onChange("the hours change", { ...item, end: item.end + direction * step })}
            />
          </FieldBlock>
        ) : (
          <FieldBlock label="Minutes">
            <Stepper
              label="Minutes"
              value={String(item.end - item.start)}
              canDown={item.end - item.start > 15}
              canUp={item.end - item.start < 180 && item.end + 15 <= 24 * 60}
              onStep={(direction) => onChange("the length change", { ...item, end: item.end + direction * 15 })}
            />
            <span className="text-[12px] text-ink-2 tabular-nums">Ends {timeLabel(item.end)}</span>
          </FieldBlock>
        )}
      </div>
      {item.free ? (
        <p className="text-[12px] text-ink-3">
          {(item.end - item.start) / group.durationMin} private {(item.end - item.start) / group.durationMin === 1 ? "session" : "sessions"} a day,{" "}
          {group.durationMin} minutes each.
        </p>
      ) : (
        <FieldBlock label="Places">
          <Chips
            label="Places"
            value={item.capacity === null ? "open" : "limit"}
            options={[
              { value: "open", label: "No limit" },
              { value: "limit", label: "Set a limit" },
            ]}
            onChange={(choice) =>
              onChange("the places change", { ...item, capacity: choice === "open" ? null : (item.capacity ?? Math.max(8, mostBooked)) })
            }
          />
          {item.capacity !== null ? (
            <Stepper
              label="Places"
              value={String(item.capacity)}
              canDown={item.capacity > 1}
              canUp={item.capacity < 500}
              onStep={(direction) => onChange("the places change", { ...item, capacity: (item.capacity ?? 1) + direction })}
            />
          ) : null}
          <span className="text-[12px] text-ink-3">
            {item.capacity === null
              ? `Anyone can book.${mostBooked ? ` ${mostBooked} booked at most.` : ""}`
              : mostBooked
                ? `${mostBooked} booked at most. The limit cannot go below that.`
                : "Bookings stop at the limit."}
          </span>
        </FieldBlock>
      )}

      {item.free ? (
        <FieldBlock label={Staff}>
          <CoachChoice
            label={words.staff}
            value={item.staffUserId ?? ""}
            options={teamOptions}
            onChange={(userId) => onChange(`the ${words.staff} change`, { ...item, staffUserId: userId || null })}
          />
        </FieldBlock>
      ) : perDay ? (
        <FieldBlock label={`${Staff} on each day`}>
          <div className="grid gap-1.5">
            {dayRows.map(({ day, holder }) => (
              <div key={`${day}|${holder.key}`} className="grid grid-cols-[34px_minmax(0,1fr)] items-center gap-2">
                <span className="text-[12px] font-bold text-ink-2">{WEEKDAY_SHORT[day]}</span>
                <CoachChoice
                  label={`${words.staff} on ${WEEKDAY_LONG[day]}`}
                  value={holder.staffUserId ?? ""}
                  options={dayOptions}
                  onChange={(userId) => {
                    setSplit(true);
                    onDayStaff(holder.key, day, userId || null);
                  }}
                />
              </div>
            ))}
          </div>
          <span className="text-[12px] text-ink-3">
            A day with another {words.staff} becomes its own block at the same time. Its bookings stay.
          </span>
        </FieldBlock>
      ) : (
        <FieldBlock label={Staff}>
          <CoachChoice
            label={words.staff}
            value={item.staffUserId ?? ""}
            options={teamOptions}
            onChange={(userId) =>
              item.days.length === 1
                ? onDayStaff(item.key, item.days[0]!, userId || null)
                : onChange(`the ${words.staff} change`, { ...item, staffUserId: userId || null })
            }
          />
          {item.days.length > 1 ? (
            <Button variant="ghost" size="sm" className="justify-self-start px-0" onClick={() => setSplit(true)}>
              A different {words.staff} on some days
            </Button>
          ) : null}
        </FieldBlock>
      )}

      {!item.free && siblings.length > 0 ? (
        <div className="grid gap-1 rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">
          <b className="text-ink">
            {group.name} at {timeLabel(item.start)} also runs:
          </b>
          {siblings.map((other) => (
            <button
              key={other.key}
              type="button"
              onClick={() => onSelect(other.key)}
              className="justify-self-start text-left font-semibold text-accent-strong underline-offset-2 hover:underline"
            >
              {listWords([...other.days].sort((a, b) => WEEK_ORDER.indexOf(a) - WEEK_ORDER.indexOf(b)).map((day) => WEEKDAY_PLURAL[day]!))} with{" "}
              {nameOf(other.staffUserId)}
            </button>
          ))}
        </div>
      ) : null}

      {bookedDays.length > 0 ? (
        <div className="grid gap-1 rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">
          <b className="text-ink">
            {item.free ? "Booked private sessions: " : `Booked ${words.people}: `}
            {listWords(bookedDays.map((row) => `${row.count} on ${WEEKDAY_PLURAL[row.day]}`))}.
          </b>
          <span>
            {changed && before && (before.start !== item.start || before.staffUserId !== item.staffUserId)
              ? item.free
                ? "Booked private sessions inside the new hours stay. Any outside get their session back."
                : `They move with this time on every date. Each ${words.family} gets one message when you publish.`
              : "If you move this time, they move with it. If you take it off, each booking gets its session back."}
          </span>
        </div>
      ) : item.free ? null : (
        <p className="text-[12px] text-ink-3">{sessionsUntil}. No one is booked yet.</p>
      )}

      <div className="flex flex-wrap gap-2">
        {changed ? (
          <Button variant="secondary" size="sm" onClick={onUndo}>
            Undo changes
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" onClick={onRemove}>
          {before === null ? "Delete" : "Take off the schedule"}
        </Button>
      </div>
    </div>
  );
}

/** A weekly time parents see that the draft takes off. */
export function RemovedItem({ item, group, onClose, onRestore }: { item: DraftItem; group: BoardGroup; onClose: () => void; onRestore: () => void }) {
  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-2">
        <i className="h-6 w-2.5 rounded" style={{ background: group.color }} aria-hidden="true" />
        <h2 className="text-[16px] font-bold">{group.name}</h2>
        <span className="rounded-full bg-cancelled-surface px-2 py-0.5 text-[10.5px] font-bold text-cancelled">Comes off</span>
        <CloseCard onClick={onClose} />
      </div>
      <p className="rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">
        {timeLabel(item.start)} on {listWords(item.days.map((day) => WEEKDAY_SHORT[day]!))} comes off the schedule when you publish.
      </p>
      <div>
        <Button variant="secondary" size="sm" onClick={onRestore}>
          Put it back
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A new level
// ---------------------------------------------------------------------------

export type NewLevel = { name: string; kind: "group" | "private"; durationMin: number; capacity: number | null; staffUserIds: string[] };

export function AddLevelForm({
  team,
  words,
  saving,
  error,
  onCancel,
  onSave,
}: {
  team: TeamPerson[];
  words: RailWords;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (level: NewLevel) => void;
}) {
  const [level, setLevel] = useState<NewLevel>({ name: "", kind: "group", durationMin: 45, capacity: null, staffUserIds: [] });
  return (
    <form
      className="grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(level);
      }}
    >
      <Back onClick={onCancel}>All {words.groupPlural}</Back>
      <h2 className="text-[16px] font-bold">New {words.group}</h2>
      <Field
        label="Name"
        size="compact"
        value={level.name}
        maxLength={120}
        placeholder="For example, Turtles"
        autoComplete="off"
        onChange={(event) => setLevel({ ...level, name: event.target.value })}
      />
      <FieldBlock label="Kind">
        <Chips
          label="Kind"
          value={level.kind}
          options={[
            { value: "group", label: "Group" },
            { value: "private", label: "Private, one to one" },
          ]}
          onChange={(kind) => setLevel({ ...level, kind, durationMin: kind === "private" ? 30 : 45 })}
        />
      </FieldBlock>
      <FieldBlock label={level.kind === "private" ? "Minutes of one session" : "Minutes"}>
        <Stepper
          label="Minutes"
          value={String(level.durationMin)}
          canDown={level.durationMin > 15}
          canUp={level.durationMin < 180}
          onStep={(direction) => setLevel({ ...level, durationMin: level.durationMin + direction * 15 })}
        />
      </FieldBlock>
      {level.kind === "group" ? (
        <>
          <FieldBlock label="Places">
            <Chips
              label="Places"
              value={level.capacity === null ? "open" : "limit"}
              options={[
                { value: "open", label: "No limit" },
                { value: "limit", label: "Set a limit" },
              ]}
              onChange={(choice) => setLevel({ ...level, capacity: choice === "open" ? null : 8 })}
            />
            {level.capacity !== null ? (
              <Stepper
                label="Places"
                value={String(level.capacity)}
                canDown={level.capacity > 1}
                canUp={level.capacity < 500}
                onStep={(direction) => setLevel({ ...level, capacity: (level.capacity ?? 1) + direction })}
              />
            ) : null}
          </FieldBlock>
          <FieldBlock label={`${words.staffPlural.charAt(0).toUpperCase()}${words.staffPlural.slice(1)}`}>
            <MultiChips
              label={words.staffPlural}
              value={level.staffUserIds}
              options={team.map(teamOption)}
              onChange={(staffUserIds) => setLevel({ ...level, staffUserIds })}
            />
            <span className="text-[12px] text-ink-3">
              {level.staffUserIds.length > 1
                ? `Pick the ${words.staff} of each time when you add it.`
                : `Pick one or more. You can also do it later.`}
            </span>
          </FieldBlock>
        </>
      ) : (
        <p className="text-[12px] text-ink-3">
          Private sessions are one {words.people.replace(/s$/, "")} each. After you add it, paint when each {words.staff} is free.
        </p>
      )}
      {error ? <p className="text-[12.5px] font-semibold text-cancelled">{error}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" loading={saving} disabled={level.name.trim() === ""}>
          Add {words.group}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// One date on the Dates view
// ---------------------------------------------------------------------------

export function DayPanel({
  date,
  draft,
  groups,
  booked,
  personName,
  onBack,
  onClose,
  onSkip,
}: {
  date: string;
  draft: TimetableDraft;
  groups: Map<string, BoardGroup>;
  booked: BookedIndex;
  personName: (userId: string) => string;
  onBack: () => void;
  onClose: (closed: boolean) => void;
  onSkip: (itemKey: string, off: boolean) => void;
}) {
  const weekday = weekdayOf(date);
  const closed = draft.closures.some((row: DraftClosure) => row.date === date && row.item === null);
  const holiday = uaePublicHoliday(date);
  const items = draft.items.filter((item) => item.days.includes(weekday)).sort((a, b) => a.start - b.start);
  return (
    <div className="grid gap-3">
      <Back onClick={onBack}>All dates</Back>
      <h2 className="text-[16px] font-bold">{formatDateKey(date)}</h2>
      {holiday ? (
        <p className="rounded-[10px] bg-late-surface px-2.5 py-2 text-[12px] text-late">
          <b>{holiday}.</b> A UAE public holiday. Is the business closed?
        </p>
      ) : null}
      <div className="flex items-center gap-3 rounded-xl border border-line bg-paper p-2.5">
        <span className="grid min-w-0 flex-1 gap-0.5 text-[11.5px] text-ink-2">
          <b className="text-[13px] text-ink">Closed on this day</b>
          No sessions. Each booking gets its session back.
        </span>
        <Switch checked={closed} onChange={onClose} label="Closed on this day" />
      </div>
      <div className="grid gap-1.5">
        <span className="text-[11.5px] font-bold text-ink-2">Sessions on this day</span>
        {items.length === 0 ? <p className="text-[12px] text-ink-3">No weekly time on {WEEKDAY_PLURAL[weekday]}.</p> : null}
        {items.map((item) => {
          const off = closed || draft.closures.some((row) => row.date === date && row.item === item.key);
          const group = groups.get(item.groupId);
          const count = bookedOnDate(item, date, weekday, booked);
          return (
            <div key={item.key} className={["flex items-center gap-2 rounded-[10px] border border-line px-2 py-1.5", off ? "bg-sunken" : ""].join(" ")}>
              <i className="h-7 w-2 shrink-0 rounded" style={{ background: group?.color }} aria-hidden="true" />
              <span className="grid min-w-0 flex-1 text-[11.5px] text-ink-2">
                <b className={["text-[13px] tabular-nums", off ? "text-ink-3 line-through" : "text-ink"].join(" ")}>
                  {item.free
                    ? `${timeLabel(item.start)} to ${timeLabel(item.end)}, ${item.staffUserId ? personName(item.staffUserId) : "no one yet"}`
                    : `${timeLabel(item.start)} ${group?.name ?? ""}`}
                </b>
                {item.free ? `Private hours${count ? `, ${count} booked` : ""}` : `${count} booked`}
              </span>
              {closed ? null : (
                <Button variant="secondary" size="sm" onClick={() => onSkip(item.key, !off)} className="whitespace-nowrap">
                  {off ? "Put back" : "Take off"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
